import { User, UserCustomAttributeType } from '@hefe/models';
import { BasketballUsersSlice, ModalsSlice, UsersSlice } from '@hefe/store';
import { Rule } from 'antd/lib/form';
import dayjs from 'dayjs';
import { t } from '@hefe/i18n';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomForm, {
  FormField,
  FormFieldType,
} from '../../../core/components/form/custom-form';

interface AddEditUserFormProps {
  user?: User;
  showSaveButton?: boolean;
}

export default function AddEditUserForm(props: AddEditUserFormProps) {
  const { user } = props;

  const [isSaving, setSaving] = useState(false);

  const dispatch = useAppDispatch();
  const customAttributes = useSelector(
    UsersSlice.usersCustomUserAttributesSelector
  );

  const submit = (values: any, resetFields: any) => {
    const submitValues = {
      ...values,
      birthday: dayjs(values.birthday).format('YYYY-MM-DD'),
      ...(user ? { id: user.id } : {}),
    };

    setSaving(true);
    if (props.user) {
      dispatch(BasketballUsersSlice.updateBasketballUser(submitValues))
        .unwrap()
        .catch(() => null)
        .finally(() => setSaving(false));
    } else {
      dispatch(ModalsSlice.modalsActions.setModalLoading(true));
      dispatch(BasketballUsersSlice.createBasketballUser(submitValues))
        .unwrap()
        .then(() => {
          dispatch(ModalsSlice.modalsActions.closeModal());
          resetFields();
        })
        .catch(() => null)
        .finally(() =>
          dispatch(ModalsSlice.modalsActions.setModalLoading(false))
        );
    }
  };

  const getCustomAttributColumns = (): FormField[] => {
    const returnValue: FormField[] = [];
    customAttributes.forEach((att) => {
      let type: FormFieldType = 'text';
      let value = user?.custom_attribute_values?.find(
        (val) => val.attribute_id === att.id
      )?.value;
      const rules: Rule[] = [];
      switch (att.type) {
        case UserCustomAttributeType.DATE:
          type = 'date';
          value = moment(value);
          break;
        case UserCustomAttributeType.NUMBER:
          type = 'number';
          break;
        case UserCustomAttributeType.MAIL:
          type = 'email';
          rules.push({
            type: 'email',
            message: t('form_errors.email'),
          });
          break;
        default:
          type = 'text';
          break;
      }
      returnValue.push({
        name: 'custom_input_' + att.id,
        label: att.name,
        type: type,
        initalValue: value,
        rules: rules,
      });
    });

    return returnValue;
  };

  const formId = `add-edit-user-form-${user ? user.id : 'new'}`;
  return (
    <CustomForm
      key={formId}
      formId={!props.showSaveButton ? formId : undefined}
      loading={isSaving}
      fields={[
        {
          name: 'sex',
          type: 'radiogroup',
          label: t('sex.title'),
          initalValue: user?.sex,
          placeholder: t('sex.title'),
          selectOptions: [
            { label: t('sex.male'), value: 1 },
            { label: t('sex.female'), value: 2 },
            { label: t('sex.diverse'), value: 3 },
          ],
          rules: [
            {
              required: true,
              type: 'number',
              message: t('form_errors.sex'),
            },
          ],
        },
        {
          name: 'firstname',
          label: t('firstname'),
          type: 'text',
          initalValue: user?.firstname,
          rules: [
            {
              required: true,
              message: t('form_errors.name'),
            },
          ],
        },
        {
          name: 'lastname',
          label: t('lastname'),
          type: 'text',
          initalValue: user?.lastname,
          rules: [
            {
              required: true,
              message: t('form_errors.name'),
            },
          ],
        },
        {
          name: 'birthday',
          label: t('birthday'),
          type: 'date',
          initalValue: user?.birthday ? moment(user.birthday) : undefined,
          rules: [],
        },
        {
          name: 'represented_by',
          label: t('represented_by'),
          type: 'text',
          initalValue: user?.represented_by,
          rules: [],
        },
        {
          name: 'nationality',
          label: t('nationality'),
          type: 'text',
          initalValue: user?.nationality,
          rules: [],
        },
        {
          name: 'street',
          label: t('street'),
          type: 'text',
          initalValue: user?.street,
          rules: [],
        },
        {
          name: 'zipcode',
          label: t('zipcode'),
          type: 'text',
          initalValue: user?.zipcode,
          rules: [],
        },
        {
          name: 'city',
          label: t('city'),
          type: 'text',
          initalValue: user?.city,
          rules: [],
        },
        {
          name: 'country',
          label: t('country'),
          type: 'text',
          initalValue: user?.country,
          rules: [],
        },
        {
          name: 'phone',
          label: t('phone'),
          type: 'text',
          initalValue: user?.phone,
          rules: [],
        },
        {
          name: 'email',
          label: t('email'),
          type: 'email',
          initalValue: user?.email,
          rules: [
            {
              type: 'email',
              message: t('form_errors.email'),
            },
          ],
        },
        {
          name: 'note',
          label: t('note'),
          type: 'text',
          initalValue: user?.note,
          rules: [],
        },
        {
          name: 'status',
          label: t('passive_member'),
          type: 'checkbox',
          initalValue: user?.status,
          rules: [],
        },
        {
          name: 'tan',
          label: t('tan'),
          extraLabel: t('information_texts.input_tan'),
          type: 'number',
          initalValue: user?.basketball_user?.tan,
          rules: [
            {
              type: 'number',
              min: 1,
              message: t('form_errors.number_min', { min: 1 }),
            },
            {
              type: 'number',
              max: 999,
              message: t('form_errors.number_max', { max: 999 }),
            },
          ],
        },
        /**
         *  Custom Attributes
         */
        ...getCustomAttributColumns(),
        {
          name: 'privacy',
          label: '',
          checkboxText:
            t('information_texts.user_consent') +
            (user ? '' : ' ' + t('information_texts.user_consent_2')),
          type: 'checkbox',
          initalValue: false,
          rules: [
            {
              required: true,
              transform: (value) => value || undefined,
              type: 'boolean',
              message: t('form_errors.user_consent'),
            },
          ],
        },
      ]}
      onSubmit={submit}
    />
  );
}
